<script lang="ts" setup>
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useSessions } from "~/composables/sessions/useSessions";
import { useFormats } from "~/composables/utils/useFormat";
import EditDatesTimeslot from "~/components/Pages/training-plan/session-details/tabs/admin/timeslots/EditDatesTimeslot.vue";
import EditTimeslotModalConfirm from "~/components/Pages/training-plan/session-details/tabs/admin/timeslots/session-timeslots-modal/EditTimeslotModalConfirm.vue";
import DeleteTimeslotModal from "~/components/Pages/training-plan/session-details/tabs/admin/timeslots/session-timeslots-modal/DeleteTimeslotModal.vue";
import DeleteWarningTimeslotModal from "~/components/Pages/training-plan/session-details/tabs/admin/timeslots/session-timeslots-modal/DeleteWarningTimeslotModal.vue";
import Tooltip from "~/components/Common/Tooltip.vue";
import { VISIOCONFERENCE_LOCATION } from "~/utils/enums/session-items.enums";
import { TimeslotConfirmModeEnum } from "~/utils/enums/sessions.enums";
import { useAddSessionFlow } from "~/composables/trainings/useAddSessionFlow";
import type { ISessionTimeslotDraft } from "~/utils/interfaces/sessions-interfaces";
import { formatTimezoneLabel } from "~/utils/helpers/timezone-labels";
import { useAuthStore } from "~/store/auth/auth.store";
import { DEFAULT_TIMEZONE } from "~/utils/helpers/timezones";

defineEmits(["delete", "save"]);

const sessionsFormats = useSessions();
const { formatTo2Decimals, toDateDisplay } = useFormats();
const props = withDefaults(
  defineProps<{
    timeslot: ISessionTimeslotDraft;
    index?: number;
  }>(),
  { index: 0 },
);

/** The session being built, whichever catalogue flow is building it. */
const flow = useAddSessionFlow();

const authStore = useAuthStore();

/** Shown whether or not the step has hours. */
const timezoneLabel = computed(() =>
  formatTimezoneLabel(props.timeslot.timezone ?? DEFAULT_TIMEZONE, props.timeslot.startDate, authStore.user?.language),
);

const openDeleteTimeslotModal = () => {
  if (flow.sessions.sessionDraft?.sessionTimeslot?.length && flow.sessions.sessionDraft?.sessionTimeslot?.length < 2) {
    flow.sessions.showWarningDeleteModal = true;
  } else {
    flow.sessions.showDeleteModal = true;
  }
};

const deleteTimeslot = async () => {
  flow.sessions.sessionDraft?.sessionTimeslot?.splice(props.index, 1);
  flow.sessions.showDeleteModal = false;
};

const openEditTimeslot = async () => {
  if (flow.sessions.sessionDraft) {
    flow.sessions.editTimeslot = props.index === flow.sessions?.editTimeslot ? null : props.index;
    flow.sessions.sessionDraft.currentSessionTimeslot = props.timeslot;
    flow.sessions.showEditTimeslotModalConfirm = null;
    flow.sessions.currentModalConfirmIndex = null;
    flow.sessions.showAddTimeslot = false;
  }
};
const save = async () => {
  if (flow.sessions.sessionDraft?.currentSessionTimeslot && flow.sessions.sessionDraft?.sessionTimeslot?.length) {
    flow.sessions.sessionDraft.sessionTimeslot[props.index] = flow.sessions.sessionDraft.currentSessionTimeslot;
    flow.sessions.editTimeslot = null;
  }
};

const openEditTimeslotModalConfirm = () => {
  if (flow.sessions?.editTimeslot !== null || flow.sessions.showAddTimeslot) {
    flow.sessions.currentModalConfirmIndex = props.index;
    flow.sessions.showEditTimeslotModalConfirm = TimeslotConfirmModeEnum.EDIT;
  } else {
    openEditTimeslot();
  }
};

const timeslotsDuration = computed(() => {
  if (flow.sessions.sessionDraft?.sessionTimeslot) {
    return flow.sessions.sessionDraft?.sessionTimeslot.reduce(
      (sum: any, timeslot: { trainingDurationHours: any }, index: number) => {
        if (index !== props.index) {
          return sum + timeslot.trainingDurationHours;
        }
        return sum;
      },
      0,
    );
  } else {
    return 0;
  }
});
</script>

<template>
  <div
    v-if="flow.sessions.sessionDraft && flow.sessions.editTimeslot !== index"
    class="flex flex-col gap-3 px-6 py-4 rounded-lg border border-stroke-color bg-light-color"
  >
    <div class="flex justify-between items-center gap-3 min-h-[29px]">
      <span class="body_demi truncate min-w-0">
        Etape {{ index + 1 }}<template v-if="timeslot.name"> - {{ timeslot.name }}</template> :
      </span>
      <div class="flex gap-3 items-center shrink-0">
        <Tooltip :position="'top-left'" append-to-body minified>
          <template #img>
            <SvgIcon
              name="edit"
              class="icon w-4 h-4 text-primary-blue-color cursor-pointer"
              @click.stop="openEditTimeslotModalConfirm"
            />
          </template>
          <template #content>
            <span class="body_regular">Modifier l'étape</span>
          </template>
        </Tooltip>
        <Tooltip :position="'top-left'" append-to-body minified>
          <template #img>
            <SvgIcon
              name="delete_trash"
              class="icon w-4 h-4 text-primary-red-color cursor-pointer"
              @click.stop="openDeleteTimeslotModal"
            />
          </template>
          <template #content>
            <span class="body_regular">Supprimer l’étape</span>
          </template>
        </Tooltip>
      </div>
    </div>

    <!-- Each date stays whole when the row wraps; below md the zone lands on its own line, so no « · ». -->
    <div class="flex flex-wrap items-center gap-1">
      <span class="whitespace-nowrap">
        <span class="body_regular text-secondary-color">Du </span>
        <span class="body_med">{{ toDateDisplay(timeslot.startDate) }} {{ timeslot.startTime }}</span>
      </span>
      <span class="whitespace-nowrap">
        <span class="body_regular text-secondary-color">au </span>
        <span class="body_med">{{ toDateDisplay(timeslot.endDate) }} {{ timeslot.endTime }}</span>
      </span>
      <span class="min-w-0 break-words">
        <span class="hidden md:inline body_regular text-grey-medium-color mx-2" aria-hidden="true">·</span>
        <span class="notranslate text-secondary-color body_regular">{{ timezoneLabel }}</span>
      </span>
    </div>

    <div class="flex flex-wrap items-center gap-x-6 gap-y-2">
      <div class="flex gap-1 items-center">
        <SvgIcon name="presential" class="w-4 h-4 text-secondary-color" />
        <span v-if="timeslot.format" class="text-secondary-color body_regular whitespace-nowrap">{{
          sessionsFormats.sessionItemFormatMap(timeslot.format)?.value
        }}</span>
        <span v-else class="text-secondary-color italic whitespace-nowrap body_regular">À définir</span>
      </div>
      <div class="flex gap-1 items-center">
        <SvgIcon name="time_normal" class="w-4 h-4 text-secondary-color" />
        <span v-if="timeslot.trainingDurationHours" class="text-secondary-color body_regular whitespace-nowrap"
          >{{ formatTo2Decimals(Number(timeslot.trainingDurationHours)) }}h</span
        >
        <span v-else class="text-secondary-color italic whitespace-nowrap body_regular">À définir</span>
      </div>
      <div class="flex gap-1 items-center min-w-0">
        <SvgIcon name="hand_hat" class="w-4 h-4 min-w-4 text-secondary-color" />
        <div v-if="timeslot.internalTrainers?.length" class="text-secondary-color body_regular truncate max-w-[600px]">
          <span v-for="(trainer, trainerIndex) in timeslot.internalTrainers" :key="trainer.userId">
            {{ trainer.displayValue }}<span v-if="Number(trainerIndex) < timeslot.internalTrainers.length - 1">, </span>
          </span>
        </div>
        <span v-else class="text-secondary-color italic whitespace-nowrap body_regular">À définir</span>
      </div>
      <div class="flex gap-1 items-center min-w-0 max-w-[350px]">
        <SvgIcon
          :name="timeslot.location === VISIOCONFERENCE_LOCATION ? 'camera' : 'pin'"
          class="w-4 h-4 min-w-4 min-h-4 text-secondary-color"
        />
        <Tooltip v-if="timeslot.selectedLocation" :position="'top-right'" append-to-body minified class="truncate">
          <template #img>
            <div class="body_regular text-secondary-color cursor-pointer truncate">
              {{ timeslot.selectedLocation.locationName }}
              <template v-if="timeslot.selectedLocation.locationManualAddress">
                - {{ timeslot.selectedLocation.locationManualAddress }}
              </template>
            </div>
          </template>
          <template #content>
            <div class="flex flex-col gap-2">
              <div class="flex flex-row gap-2 items-center">
                <SvgIcon
                  :name="timeslot.location === VISIOCONFERENCE_LOCATION ? 'camera' : 'pin'"
                  class="w-4 h-4 text-secondary-color"
                ></SvgIcon>
                <span class="body_regular">{{ timeslot.selectedLocation.locationName }}</span>
                <span v-if="timeslot.selectedLocation.locationManualAddress" class="body_regular"
                  >- {{ timeslot.selectedLocation.locationManualAddress }}</span
                >
              </div>
              <div v-if="timeslot.selectedRoom" class="flex flex-row gap-2 items-center">
                <div class="flex flex-row gap-1 items-center">
                  <SvgIcon name="target-menu" class="w-4 h-4"></SvgIcon>
                  <span class="body_regular">{{ timeslot.selectedRoom.roomName }}</span>
                </div>
                <span v-if="timeslot.selectedRoom.roomAccess" class="body_regular">
                  - {{ timeslot.selectedRoom.roomAccess }}</span
                >
                <div v-if="timeslot.selectedRoom.roomEquipment" class="flex flex-row gap-1 items-center">
                  <SvgIcon name="equipment" class="w-4 h-4"></SvgIcon>
                  <span class="body_regular">{{ timeslot.selectedRoom.roomEquipment }}</span>
                </div>
              </div>
            </div>
          </template>
        </Tooltip>
        <span v-else-if="timeslot.location" class="text-secondary-color body_regular truncate capitalize">{{
          timeslot.location
        }}</span>
        <span v-else class="text-secondary-color italic whitespace-nowrap body_regular">À définir</span>
      </div>
    </div>
  </div>
  <EditDatesTimeslot
    v-else-if="flow.sessions.sessionDraft && flow.sessions.sessionDraft.currentSessionTimeslot"
    :tmp-session-timeslots="flow.sessions.sessionDraft.currentSessionTimeslot"
    :async-action="save"
    :trainingDuration="flow.training.durationHours"
    :timeslotsDuration="timeslotsDuration"
    @close="openEditTimeslot"
  ></EditDatesTimeslot>
  <EditTimeslotModalConfirm
    v-if="
      flow.sessions.showEditTimeslotModalConfirm === TimeslotConfirmModeEnum.EDIT &&
      flow.sessions.currentModalConfirmIndex !== null &&
      props.index === flow.sessions.currentModalConfirmIndex
    "
    @cancel="flow.sessions.showEditTimeslotModalConfirm = null"
    @confirm="openEditTimeslot"
  />
  <DeleteTimeslotModal
    v-if="flow.sessions.showDeleteModal"
    @cancel="flow.sessions.showDeleteModal = false"
    @delete="deleteTimeslot"
  />
  <DeleteWarningTimeslotModal
    v-if="flow.sessions.showWarningDeleteModal"
    @cancel="flow.sessions.showWarningDeleteModal = false"
  />
</template>
